'use client';

import { motion } from 'framer-motion';
import { 
  TrendingUp, 
  Activity, 
  Shield, 
  Zap,
  Clock,
  AlertCircle
} from 'lucide-react';
import StatsCard, { StatsGroup } from './StatsCard';
import NetworkTopology from './NetworkTopology';
import DeviceTable from './DeviceTable';
import { cn } from '@/lib/utils';

// 带宽使用趋势组件
function BandwidthChart() {
  const data = [
    { time: '00:00', value: 30 },
    { time: '04:00', value: 45 },
    { time: '08:00', value: 80 },
    { time: '12:00', value: 95 },
    { time: '16:00', value: 75 },
    { time: '20:00', value: 60 },
    { time: '24:00', value: 40 },
  ];

  const maxValue = Math.max(...data.map(d => d.value));
  const points = data.map((d, i) => ({
    x: (i / (data.length - 1)) * 100,
    y: 100 - (d.value / maxValue) * 100
  }));

  const pathD = points.map((p, i) => 
    `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
  ).join(' ');

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.25 }}
      className="bg-white rounded-xl border border-[var(--border)] p-5"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[var(--info)]/10 flex items-center justify-center">
            <Activity className="w-5 h-5 text-[var(--info)]" />
          </div>
          <div>
            <h3 className="font-semibold text-[var(--foreground)]">带宽使用趋势</h3>
            <p className="text-xs text-[var(--muted-foreground)]">过去24小时</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-2xl font-semibold text-[var(--foreground)]">67%</span>
          <span className="text-xs text-[var(--success)] flex items-center">
            <TrendingUp className="w-3 h-3 mr-0.5" />
            +5.2%
          </span>
        </div>
      </div>

      {/* 简化的图表 */}
      <div className="h-32 relative">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
          <defs>
            <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.3" />
              <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d={`${pathD} L 100 100 L 0 100 Z`}
            fill="url(#gradient)"
          />
          <path
            d={pathD}
            fill="none"
            stroke="var(--primary)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {points.map((p, i) => (
            <circle
              key={i}
              cx={p.x}
              cy={p.y}
              r="2"
              fill="white"
              stroke="var(--primary)"
              strokeWidth="1.5"
            />
          ))}
        </svg>
        
        {/* X轴标签 */}
        <div className="absolute bottom-0 left-0 right-0 flex justify-between text-xs text-[var(--muted-foreground)] pt-2">
          {data.map((d, i) => (
            <span key={i}>{d.time}</span>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

// 告警列表组件
function AlertList() {
  const alerts = [
    { id: 1, device: '核心交换机-B', message: 'CPU使用率超过80%', level: 'warning', time: '5分钟前' },
    { id: 2, device: '无线AP-会议室', message: '设备已离线', level: 'error', time: '2小时前' },
    { id: 3, device: '防火墙-主', message: '检测到异常流量', level: 'warning', time: '30分钟前' },
    { id: 4, device: '汇聚交换机-2', message: '端口错误率过高', level: 'warning', time: '1小时前' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.35 }}
      className="bg-white rounded-xl border border-[var(--border)] p-5"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[var(--destructive)]/10 flex items-center justify-center">
            <AlertCircle className="w-5 h-5 text-[var(--destructive)]" />
          </div>
          <div>
            <h3 className="font-semibold text-[var(--foreground)]">最近告警</h3>
            <p className="text-xs text-[var(--muted-foreground)]">需要关注的设备异常</p>
          </div>
        </div>
        <button className="text-sm text-[var(--primary)] hover:underline">
          查看全部
        </button>
      </div>

      <div className="space-y-3">
        {alerts.map((alert) => (
          <div 
            key={alert.id}
            className="flex items-start gap-3 p-3 rounded-lg bg-[var(--secondary)]/50 hover:bg-[var(--secondary)] transition-colors cursor-pointer"
          >
            <div className={cn(
              "w-2 h-2 rounded-full mt-1.5 flex-shrink-0",
              alert.level === 'error' ? "bg-[var(--destructive)]" : "bg-[var(--warning)]"
            )} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[var(--foreground)] truncate">{alert.device}</p>
              <p className="text-xs text-[var(--muted-foreground)] truncate">{alert.message}</p>
            </div>
            <span className="text-xs text-[var(--muted-foreground)] flex-shrink-0">{alert.time}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

// 系统状态组件
function SystemStatus() {
  const services = [
    { name: 'SNMP服务', status: 'running', uptime: '99.9%' },
    { name: 'Syslog服务', status: 'running', uptime: '99.8%' },
    { name: 'NetFlow服务', status: 'running', uptime: '99.5%' },
    { name: '告警服务', status: 'running', uptime: '99.9%' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.4 }}
      className="bg-white rounded-xl border border-[var(--border)] p-5"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[var(--success)]/10 flex items-center justify-center">
            <Shield className="w-5 h-5 text-[var(--success)]" />
          </div>
          <div>
            <h3 className="font-semibold text-[var(--foreground)]">系统状态</h3>
            <p className="text-xs text-[var(--muted-foreground)]">核心服务运行状况</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-[var(--success)] animate-pulse-slow" />
          <span className="text-sm text-[var(--success)]">运行正常</span>
        </div>
      </div>

      <div className="space-y-3">
        {services.map((service) => (
          <div 
            key={service.name}
            className="flex items-center justify-between py-2"
          >
            <div className="flex items-center gap-3">
              <Zap className="w-4 h-4 text-[var(--muted-foreground)]" />
              <span className="text-sm text-[var(--foreground)]">{service.name}</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-xs text-[var(--success)]">{service.uptime}</span>
              <div className="w-16 h-1.5 bg-[var(--secondary)] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[var(--success)] rounded-full"
                  style={{ width: service.uptime }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 pt-4 border-t border-[var(--border)]">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-[var(--muted-foreground)]">
            <Clock className="w-4 h-4" />
            <span>系统运行时间</span>
          </div>
          <span className="font-medium text-[var(--foreground)]">128天 14小时 32分</span>
        </div>
      </div>
    </motion.div>
  );
}

export default function Dashboard() {
  return (
    <div className="space-y-6">
      {/* 统计卡片 */}
      <StatsGroup />

      {/* 网络拓扑 */}
      <NetworkTopology />

      {/* 第二行：带宽趋势 + 告警列表 + 系统状态 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <BandwidthChart />
        </div>
        <div className="lg:col-span-1">
          <AlertList />
        </div>
        <div className="lg:col-span-1">
          <SystemStatus />
        </div>
      </div>

      {/* 设备列表 */}
      <DeviceTable />
    </div>
  );
}
